import { apiClient } from './config.js';

/** Editorial Commissions V1 (EDITORIAL_COMMISSIONS_PLAN §7.4). */

export interface CommissionPart {
  part_id: string;
  commission_id: string;
  part_number: number;
  working_title: string;
  angle: string | null;
  trigger_type: 'manual' | 'next_filing' | 'date';
  trigger_date: string | null;
  status: 'pending' | 'triggered' | 'generated' | 'published' | 'skipped';
  effective_status?: string;
  plan_item_id: string | null;
  article_id: string | null;
  announcement_id: string | null;
  derived_article_id?: string | null;
  derived_publish_status?: string | null;
  derived_article_title?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Commission {
  commission_id: string;
  brief_text: string;
  entity_id: string | null;
  target_id: string;
  product_id: string | null;
  persona_slug: string;
  content_class: 'feature' | 'series' | 'arc' | 'explainer' | 'one_off';
  status: 'proposed' | 'approved' | 'in_progress' | 'complete' | 'retired';
  proposal: Record<string, any> | string;
  anchor_policy: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  parts?: CommissionPart[];
  // list-view enrichments
  entity_display_name?: string | null;
  entity_identifier?: string | null;
  target_name?: string;
  part_count?: number;
  pending_part_count?: number;
}

export async function parseCommissionBrief(
  briefText: string,
  entityId?: string
): Promise<Commission> {
  const { data } = await apiClient.post('/commissions/parse', {
    brief_text: briefText,
    entity_id: entityId ?? null,
  });
  return data;
}

export async function listCommissions(params?: {
  status?: string;
  entity_id?: string;
}): Promise<Commission[]> {
  const { data } = await apiClient.get('/commissions', { params });
  return data;
}

export async function getCommission(id: string): Promise<Commission> {
  const { data } = await apiClient.get(`/commissions/${id}`);
  return data;
}

export async function updateCommission(
  id: string,
  fields: Partial<
    Pick<
      Commission,
      | 'brief_text'
      | 'entity_id'
      | 'target_id'
      | 'product_id'
      | 'persona_slug'
      | 'content_class'
      | 'anchor_policy'
    >
  > & { parts?: Array<Partial<CommissionPart> & { part_id: string }> }
): Promise<Commission> {
  const { data } = await apiClient.patch(`/commissions/${id}`, fields);
  return data;
}

export async function approveCommission(id: string): Promise<Commission> {
  const { data } = await apiClient.post(`/commissions/${id}/approve`);
  return data;
}

export async function triggerCommissionPart(
  commissionId: string,
  partId: string
): Promise<{ status: string; part_id: string }> {
  const { data } = await apiClient.post(
    `/commissions/${commissionId}/parts/${partId}/trigger`
  );
  return data;
}

export async function skipCommissionPart(
  commissionId: string,
  partId: string
): Promise<{ status: string; part_id: string }> {
  const { data } = await apiClient.post(
    `/commissions/${commissionId}/parts/${partId}/skip`
  );
  return data;
}

export async function retireCommission(
  id: string
): Promise<{ status: string; commission_id: string }> {
  const { data } = await apiClient.post(`/commissions/${id}/retire`);
  return data;
}
